import React from "react";
import { Briefcase, Users, CalendarClock, Trophy, Star } from "lucide-react";
import { Badge, STATUS_STYLE, KIND_BADGE, fmtDateTime, INTERVIEW_STATUS } from "./recruitLabels";

const Kpi = ({ icon: Icon, label, value, sub, color = "#002FA7", testid }) => (
  <div className="bg-white border border-[#DEE2E6] p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color }}>{label}</div>
      <Icon className="h-4 w-4" style={{ color }} />
    </div>
    <div className="font-display font-black text-3xl mt-1">{value}</div>
    {sub && <div className="text-[10px] text-[#868E96] mt-0.5">{sub}</div>}
  </div>
);

export const RecruitOverview = ({ data, onOpenCandidate }) => {
  if (!data) return <div className="text-xs text-[#868E96]">Chargement…</div>;
  const c = data.candidates;
  return (
    <div className="space-y-6" data-testid="recruit-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi icon={Briefcase} label="Offres publiées" value={data.offers.published} sub={`${data.offers.draft} brouillon(s) · ${data.offers.closed} fermée(s)`} testid="kpi-offers" />
        <Kpi icon={Users} label="Candidats" value={c.total} sub={`${c.new_7d} sur 7 jours · ${c.by_kind.business_partner} BP`} color="#0EA5E9" testid="kpi-candidates" />
        <Kpi icon={CalendarClock} label="Entretiens 7 j" value={data.interviews_upcoming.length} color="#7C3AED" testid="kpi-interviews" />
        <Kpi icon={Trophy} label="Taux d'acceptation" value={`${c.accept_rate} %`} sub={`${c.by_status.accepted} accepté(s)`} color="#059669" testid="kpi-accept" />
        <Kpi icon={Star} label="Délai de décision" value={c.avg_days_to_decision == null ? "—" : `${c.avg_days_to_decision} j`} sub="moyenne" color="#F59E0B" testid="kpi-delay" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DEE2E6]">
          <div className="px-4 py-3 border-b border-[#DEE2E6] text-[10px] uppercase tracking-[0.25em] font-bold">Pipeline</div>
          <div className="p-4 grid grid-cols-3 gap-2" data-testid="overview-pipeline">
            {Object.entries(STATUS_STYLE).map(([k, s]) => (
              <div key={k} className={`border-t-4 ${s.col} bg-[#F8F9FA] p-3`} data-testid={`pipeline-${k}`}>
                <div className="font-display font-black text-2xl">{c.by_status[k] || 0}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#495057]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#DEE2E6]">
          <div className="px-4 py-3 border-b border-[#DEE2E6] text-[10px] uppercase tracking-[0.25em] font-bold">Prochains entretiens (7 jours)</div>
          {data.interviews_upcoming.length === 0 ? <div className="p-6 text-xs text-[#868E96]" data-testid="overview-no-interviews">Aucun entretien planifié cette semaine.</div> : (
            <div className="divide-y divide-[#DEE2E6]">
              {data.interviews_upcoming.map((i) => (
                <button key={i.id} type="button" onClick={() => onOpenCandidate(i.candidate_id)} className="w-full text-left px-4 py-2.5 hover:bg-[#F8F9FA] flex items-center gap-3" data-testid={`overview-itw-${i.id}`}>
                  <div className="text-xs font-mono text-[#495057] w-36 shrink-0">{fmtDateTime(i.scheduled_at)}</div>
                  <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{i.candidate_name}</div><div className="text-[10px] text-[#868E96] truncate">{i.offer_title} · {i.mode}</div></div>
                  <Badge cfg={INTERVIEW_STATUS[i.status]} />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#DEE2E6]">
        <div className="px-4 py-3 border-b border-[#DEE2E6] text-[10px] uppercase tracking-[0.25em] font-bold">Dernières candidatures</div>
        {data.recent.length === 0 ? <div className="p-6 text-xs text-[#868E96]" data-testid="overview-no-recent">Aucune candidature pour l'instant — publiez une offre et partagez la page Carrières.</div> : (
          <div className="divide-y divide-[#DEE2E6]">
            {data.recent.map((r) => (
              <button key={r.id} type="button" onClick={() => onOpenCandidate(r.id)} className="w-full text-left px-4 py-2.5 hover:bg-[#F8F9FA] flex flex-wrap items-center gap-3" data-testid={`overview-cand-${r.id}`}>
                <span className="text-[10px] font-mono text-[#868E96]">{r.reference}</span>
                <span className="text-sm font-semibold">{r.prenom} {r.nom}</span>
                <span className="text-xs text-[#495057] flex-1 truncate">{r.offer_title}</span>
                <Badge cfg={KIND_BADGE[r.kind]} />
                <Badge cfg={STATUS_STYLE[r.status]} />
                {r.rating != null && <span className="text-[10px] font-bold text-[#F59E0B]">★ {r.rating}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
